import { useEffect, useRef } from "react";
import { resolvePendingPayments } from "../utils/pendingPayments.js";

// No se consulta más seguido que esto aunque la red suba y baje: cada consulta es una llamada al backend.
export const PENDING_PAYMENT_RECOVERY_MIN_INTERVAL_MS = 60 * 1000;

/**
 * SOK-04 (punto 5): con red y sin cobro en pantalla, vuelve a consultar los cobros que quedaron sin
 * confirmar (kiosk_pending_payment) y los borra cuando terminaron. Corre al abrir la app, al volver
 * la red y al cerrarse el modal de pago.
 *
 * No toca el carro ni el modal: puede haber otro cliente comprando. El resultado queda en activity
 * ('payment-recovery'); un pago aprobado que el tótem no alcanzó a confirmar se ve ahí y en el carro,
 * que el backend actualiza al consultarlo.
 *
 * @param {object} params
 * @param {boolean} params.enabled - Con red, sin modal de pago abierto y con la instancia cargada.
 * @param {(record: object, signal?: AbortSignal) => Promise<object>} params.check - checkPendingPaymentInBackground.
 * @param {(record: object, result: object) => void} [params.onResult]
 */
export function usePendingPaymentRecovery({ enabled, check, onResult }) {
  // Las funciones cambian en cada render del padre; se leen desde un ref para no relanzar la consulta.
  const callbacksRef = useRef({ check, onResult });
  callbacksRef.current = { check, onResult };
  const lastRunRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    if (Date.now() - lastRunRef.current < PENDING_PAYMENT_RECOVERY_MIN_INTERVAL_MS) return;
    lastRunRef.current = Date.now();
    const controller = new AbortController();
    resolvePendingPayments({
      check: (record, signal) => callbacksRef.current.check(record, signal),
      onResult: (record, result) => callbacksRef.current.onResult?.(record, result),
      signal: controller.signal,
    }).catch((error) => console.warn('No se pudieron revisar los cobros pendientes:', error));
    // Se pierde la red o se abre un cobro: se deja de consultar; lo que quedó se revisa la próxima vez.
    return () => controller.abort();
  }, [enabled]);
}
